import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import analyticsUtils from 'analytics/utils';
import { sendSOTLinkTrackingClickEvent } from 'analytics/utils/sotTracking';
import orderUtils from 'utils/Order';
import localeUtils from 'utils/LanguageLocale';
import {
    SDD_ERRORS, PAYMENT_ERRORS, BOPIS_PICKUP_ERRORS, SECTION_NAMES, FC_CHECKOUT_SECTIONS
} from 'constants/frictionlessCheckout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import ErrorConstants from 'utils/ErrorConstants';
import store from 'store/Store';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import FrictionlessCheckoutEN from 'components/FrictionlessCheckout/locales/FrictionlessCheckout_en_US';
import FrictionlessCheckoutFR from 'components/FrictionlessCheckout/locales/FrictionlessCheckout_fr_CA';
import { normalizeCardTypeForAnalytics } from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/paymentAnalyticsUtils';

const { hasHalAddress } = orderUtils;
const { getLocalisedDataFromResource } = localeUtils;
const getText = getLocalisedDataFromResource(FrictionlessCheckoutEN, FrictionlessCheckoutFR);

const PLACE_ORDER_PAGE_NAME = 'checkout:place order:n/a:*';
const CHECKOUT_PAYMENT_PAGE_DETAIL = anaConsts.PAGE_TYPES.PAYMENT;
const paymentButtonState = { tracked: false };

// Allowlist of valid payment button texts for tracking.
// Normalized to lowercase to ensure case-insensitive matching.
// Hoisted to module scope to avoid recreating the Set on every function call.
const ALLOWED_PAYMENT_BUTTON_TEXTS = new Set(
    [
        anaConsts.ACTION_INFO.PAYMENT_ADD_NEW_CARD,
        anaConsts.ACTION_INFO.PAYMENT_USE_THIS_CARD,
        anaConsts.ACTION_INFO.PAYMENT_USE_THIS_PAYMENT_METHOD
    ].map(text => text.toLowerCase())
);

function normalizePaymentButtonText(buttonText = '') {
    const text = String(buttonText).trim().toLowerCase();

    // Only allow the explicitly requested CTA texts to avoid polluting pageName
    return ALLOWED_PAYMENT_BUTTON_TEXTS.has(text) ? text : '';
}

/**
 * Maps section names to the technical paths we use for analytics.
 * This makes sure pageName uses the internal identifier (like "shipping")
 * instead of the label users see (like "deliver to") since checkout page
 * is broken into multiple sections.
 *
 * @param {string} sectionName - A section name from SECTION_NAMES
 * @returns {string} The technical path for that section
 */

function getSectionTechnicalPath(sectionName) {
    // Create reverse lookup from sectionName to path
    const sectionMapping = Object.values(FC_CHECKOUT_SECTIONS).reduce((acc, section) => {
        acc[section.sectionName] = section.path;

        return acc;
    }, {});

    // Return the technical path, or fallback to the section name
    return sectionMapping[sectionName] || sectionName?.toLowerCase().replace(/_/g, ' ');
}

function setAnalyticsForNextCall(pageName, pageDetail) {
    // Update page name so subsequent PAGE_LOAD and ASYNC_PAGE_LOAD calls have the proper previous page name (c6).
    if (digitalData.page.attributes.initialPageLoadDidOccur) {
        digitalData.page.attributes.sephoraPageInfo.pageName = pageName;
        digitalData.page.pageInfo.pageName = pageDetail;
        analyticsUtils.setNextPageData({ pageName });
    } else {
        const dataToStore = {
            pageName,
            pageDetail
        };
        Storage.local.setItem(LOCAL_STORAGE.FRICTIONLESS_CHECKOUT_PAGE_ANALYTICS, dataToStore);
        digitalData.page.pageInfo.pageName = pageDetail;
    }

    window?.addEventListener(
        'postPageLoad',
        () => {
            if (digitalData.page.attributes.initialPageLoadDidOccur) {
                const { pageName: previousSectionPageName, pageDetail: previousSectionPageDetail } =
                    Storage.local.getItem(LOCAL_STORAGE.FRICTIONLESS_CHECKOUT_PAGE_ANALYTICS) || {};
                digitalData.page.attributes.sephoraPageInfo.pageName = previousSectionPageName;
                digitalData.page.pageInfo.pageName = previousSectionPageDetail;
                analyticsUtils.setNextPageData({ pageName: previousSectionPageName });
                Storage.local.removeItem(LOCAL_STORAGE.FRICTIONLESS_CHECKOUT_PAGE_ANALYTICS);
            }
        },
        { once: true }
    );
}

/**
 * Fires an ASYNC_PAGE_LOAD (s.t() TrackState) when the checkout payment section enters a new state.
 *
 * @param {string} pageDetail - Page detail (e.g., 'payment', 'shipping')
 * @param {boolean} isTriggeredByUserClick - Whether this was triggered by user click
 * @param {boolean} shouldAddEvent247 - Whether to add EVENT_247
 * @param {string} [buttonText] - Optional button text for non-default payment states (e.g., 'add new card')
 *                                When provided, appends 'button=<text>' to pageName
 * @param {Object} [extraData] - Optional additional data to merge into the analytics payload (e.g., { eVar140: '...' })
 */
function setChangeLinkAnalytics(pageDetail, isTriggeredByUserClick, shouldAddEvent247 = false, buttonText = '', extraData = {}) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;

    // Normalize and validate button text
    const normalizedButtonText = buttonText ? normalizePaymentButtonText(buttonText) : '';

    // Build pageName with optional button suffix for non-default payment states
    const buttonSuffix = normalizedButtonText ? `button=${normalizedButtonText}` : '';
    const pageName = `${pageType}:${pageDetail}:n/a:*${buttonSuffix}`.toLowerCase();

    const eventStrings = [anaConsts.Event.SC_CHECKOUT];

    if (hasHalAddress() && shouldAddEvent247) {
        eventStrings.push(anaConsts.Event.EVENT_247);
    }

    const data = {
        pageType,
        pageName,
        eventStrings,
        pageDetail,
        previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName || PLACE_ORDER_PAGE_NAME,
        ...extraData
    };

    if (isTriggeredByUserClick) {
        const prop55 = `checkout-place order:${pageDetail}:change`.toLowerCase();
        data.linkData = prop55;
    }

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, { data });

    setAnalyticsForNextCall(pageName, pageDetail);
}

/**
 * Fires an ASYNC_PAGE_LOAD (s.t() TrackState) when the checkout payment section is open
 * and a non-default CTA button state is visible to the user.
 *
 * PageName format:
 *   checkout:payment:n/a:*button=<cta text>
 *
 * @param {string} buttonText - Button text (e.g., 'add new card', 'use this card')
 * @param {string} [unavailableMessage] - Optional unavailable payment methods message for eVar140
 *
 * Note: This delegates to setChangeLinkAnalytics with the buttonText parameter
 * to ensure only ONE ASYNC_PAGE_LOAD event fires (not two).
 */
function setPaymentSectionButtonStateAnalytics(buttonText, unavailableMessage = '') {
    const normalizedButtonText = normalizePaymentButtonText(buttonText);

    if (!normalizedButtonText) {
        return;
    }

    if (paymentButtonState.tracked) {
        return;
    }

    paymentButtonState.tracked = true;

    const extraData = unavailableMessage ? { eVar140: unavailableMessage } : {};

    setChangeLinkAnalytics(CHECKOUT_PAYMENT_PAGE_DETAIL, false, false, normalizedButtonText, extraData);
}

/**
 * Sets the payment pageName with button suffix for the initial PAGE_LOAD
 * without firing an ASYNC_PAGE_LOAD. Used when NewUserPaymentView mounts
 * before the initial page load has occurred (Case 2: saved address, no saved payment).
 *
 * @param {string} buttonText - Button text (e.g., 'add new card') appended as 'button=<text>' to pageName
 */
function setPaymentPageNameForInitialLoad(buttonText) {
    const normalizedButtonText = normalizePaymentButtonText(buttonText);

    if (!normalizedButtonText) {
        return;
    }

    paymentButtonState.tracked = true;

    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const buttonSuffix = `button=${normalizedButtonText}`;
    const pageName = `${pageType}:${CHECKOUT_PAYMENT_PAGE_DETAIL}:n/a:*${buttonSuffix}`.toLowerCase();

    setAnalyticsForNextCall(pageName, CHECKOUT_PAYMENT_PAGE_DETAIL);
    digitalData.page.pageInfo.pageName = pageName;
}

/**
 * Resets the payment button state tracking flag so payment CTA state analytics
 * can fire again when the payment section is reopened (e.g., user clicks Change).
 */
function resetPaymentButtonStateTracking() {
    paymentButtonState.tracked = false;
}

function setAlternatePickupAnalytics(analyticsActionInfo, type = anaConsts.LINK_TRACKING_EVENT) {
    const isLinkTracking = type === anaConsts.LINK_TRACKING_EVENT;
    const isPageLoad = type === anaConsts.ASYNC_PAGE_LOAD;

    const payload = {
        data: {
            pageName: anaConsts.ALT_PICKUP.PAGE_NAME,
            pageType: anaConsts.PAGE_TYPES.BOPIS_CHECKOUT,
            pageDetail: anaConsts.PAGE_DETAIL.ALTERNATE_PICKUP
        }
    };

    if (isLinkTracking) {
        payload.data.linkName = analyticsActionInfo;
        payload.data.actionInfo = analyticsActionInfo;
    } else if (isPageLoad) {
        payload.data.linkData = analyticsActionInfo;
        payload.data.previousPageName = digitalData.page.attributes.previousPageData.pageName;
    }

    return processEvent.process(type, payload);
}

function setGiftCardPaymentAnalytics() {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = anaConsts.PAGE_NAMES.GIFT_CARD_PAYMENT;

    const eventStrings = [anaConsts.Event.SC_CHECKOUT];
    const pageName = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageDetail,
            eventStrings,
            pageName,
            previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName
        }
    });

    setAnalyticsForNextCall(pageName, pageDetail);
}

function setSubstitueModalClickAnalytics() {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = anaConsts.PAGE_TYPES.ITEM_SUBSTITUTION_MODAL;
    const pageName = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageName,
            pageDetail
        }
    });
}

function linkTrackingEventForErrors(componentName, message) {
    if (Array.isArray(message) && message?.length > 0) {
        message.forEach(msg => {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:n/a:${componentName.replaceAll('_', ' ')}`.toLowerCase(), //prop28
                    errorMessages: msg?.toLowerCase(), //prop48
                    actionInfo: anaConsts.ACTION_INFO.PLACE_ORDER_BTN_CLICK //prop55
                }
            });
        });
    } else {
        const data = {
            fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:n/a:${componentName.replaceAll('_', ' ')}`.toLowerCase(), //prop28
            errorMessages: message?.toLowerCase(), //prop48
            actionInfo: anaConsts.ACTION_INFO.PLACE_ORDER_BTN_CLICK //prop55
        };

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data
        });
    }
}

function trackPlaceOrderErrorAnalytics(errorData) {
    const { key, errorMessages } = errorData;

    const defaultErrorMessage = getText('defaultErrorMessage');

    let errorZoneDescription = 'modal';

    if (key) {
        if (SDD_ERRORS.includes(key)) {
            errorZoneDescription = SECTION_NAMES.SDD;
        } else if (PAYMENT_ERRORS.includes(key)) {
            errorZoneDescription = SECTION_NAMES.PAYMENT;
        } else if (BOPIS_PICKUP_ERRORS.includes(key)) {
            errorZoneDescription = SECTION_NAMES.BOPIS_PICKUP_INFO;
        }
    }

    const userDisplayedMessage = errorMessages?.length > 0 ? errorMessages.join(' ') : defaultErrorMessage;

    const analyticsData = {
        fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:${key || 'api.error'}:${errorZoneDescription.replaceAll(
            '_',
            ' '
        )}`.toLowerCase(), //prop28
        errorMessages: userDisplayedMessage?.toLowerCase(), // prop48,
        actionInfo: anaConsts.ACTION_INFO.PLACE_ORDER_BTN_CLICK // prop55
    };

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: analyticsData
    });
}

function errorTrackingBeforeAPICall(errors) {
    Object.entries(errors).forEach(([key, value]) => {
        linkTrackingEventForErrors(key, value);
    });
}

function setPickupInfoPageNameForInitialLoad(isComplete = false) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = `pickup info ${isComplete ? 'edit' : 'add'}`;
    const pageName = `${pageType}:${pageDetail}:n/a:*`;

    setAnalyticsForNextCall(pageName, pageDetail);
    digitalData.page.pageInfo.pageName = pageName;
}

function setAddOrEditPickupInfoAnalytics(isComplete = false) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = `pickup info ${isComplete ? 'edit' : 'add'}`;
    const pageName = `${pageType}:${pageDetail}:n/a:*`;
    const eventStrings = [anaConsts.Event.SC_CHECKOUT];

    const data = {
        pageType,
        pageDetail,
        eventStrings,
        pageName,
        previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName || PLACE_ORDER_PAGE_NAME
    };

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, { data });
    setAnalyticsForNextCall(pageName, pageDetail);
}

function setAddOrEditShippingAddressAnalytics(isEditAddress = false, linkText = '', isGiftCardSection, hasUserOpenedAddressForm) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const giftCardShipping = anaConsts.PAGE_DETAIL.GIFT_CARD_SHIPPING;
    let pageDetail = '';

    if (isGiftCardSection) {
        if (hasUserOpenedAddressForm) {
            pageDetail = `${giftCardShipping}-${isEditAddress ? 'edit' : 'add'} address`;
        } else {
            pageDetail = giftCardShipping;
        }
    } else {
        pageDetail = `shipping-${isEditAddress ? 'edit' : 'add'} address`;
    }

    const link = `${pageType}:${pageDetail}:${linkText.toLowerCase()}`;
    const pageName = `${pageType}:${pageDetail}:n/a:*`;
    const eventStrings = [anaConsts.Event.SC_CHECKOUT];

    const data = {
        pageType,
        pageDetail,
        eventStrings,
        linkName: link,
        pageName,
        previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName || PLACE_ORDER_PAGE_NAME
    };

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, { data });

    setAnalyticsForNextCall(pageName, pageDetail);
}

function setPlaceOrderBtnClickanalytics({ isSuccess, hasErrorMessage }) {
    const paymentMethod = FrictionlessUtils.getPaymentMethodName();

    let actionInfo = anaConsts.ACTION_INFO.CHECKOUT_PLACE_ORDER;

    if (paymentMethod?.length > 0) {
        actionInfo += `:${paymentMethod}`;
    }

    const data = {
        actionInfo: actionInfo?.toLowerCase()
    };

    if (!isSuccess && !hasErrorMessage) {
        data.eventStrings = anaConsts.Event.EVENT_294;
    }

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data
    });
}

//Parse through the error object to get what we need in the format we need it in
function getErrorMessages(errors = {}) {
    const errorMessages = [];
    const ERROR_LEVEL = ErrorConstants.ERROR_LEVEL;
    Object.keys(ERROR_LEVEL).forEach(errorLevel => {
        Object.keys(errors[errorLevel]).forEach(errorKey => {
            errorMessages.push(errors[errorLevel][errorKey].message);
        });
    });

    return errorMessages;
}

function setSectionLevelErrorAnalytics(componentName, componentErrors = []) {
    const errors = store.getState().errors;
    const errorMsgs = componentErrors.length ? componentErrors : getErrorMessages(errors);

    if (Array.isArray(errorMsgs) && errorMsgs?.length > 0) {
        errorMsgs.forEach(message => {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:n/a:${componentName.replaceAll('_', ' ')}`.toLowerCase(), //prop28
                    errorMessages: message?.toLowerCase()
                }
            });
        });
    } else {
        const data = {
            fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:n/a:${componentName.replaceAll('_', ' ')}`.toLowerCase(), //prop28
            errorMessages: Array.isArray(errorMsgs) ? '' : errorMsgs?.toLowerCase() || ''
        };
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data
        });
    }
}

function setAnalyticsForOrderConfirmation(defaultPayment, isDefaultPaymentBeingUsed) {
    const nextPageData = {
        defaultPayment
    };

    if (isDefaultPaymentBeingUsed) {
        nextPageData.events = [anaConsts.Event.DEFAULT_PAYMENT_METHOD_USED];
    }

    analyticsUtils.setNextPageData(nextPageData);
}

function thisAddressButtonAnalytics(sectionName, isDefaultChecked = false) {
    const isGiftCard = sectionName === SECTION_NAMES.GIFT_CARD_DELIVERY;

    // User-facing display name for prop55 (e.g., "deliver to")
    const displaySection = isGiftCard ? 'gift card' : sectionName?.toLowerCase().replace(/_/g, ' ');

    // Technical path for pageName and pageDetail (e.g., "shipping")
    const technicalPath = isGiftCard ? anaConsts.PAGE_DETAIL.GIFT_CARD_SHIPPING : getSectionTechnicalPath(sectionName);

    const prop55 = `${displaySection}:use this address`;
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageName = `${pageType}:${technicalPath}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            linkName: prop55,
            actionInfo: prop55,
            previousPage: PLACE_ORDER_PAGE_NAME,
            pageType,
            pageName,
            pageDetail: technicalPath,
            ...(isDefaultChecked && { eventStrings: ['event121'] })
        }
    });
    setAnalyticsForNextCall(pageName, technicalPath);
}

function newDefaultAddressAnalytics(pageName) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = `shipping-${pageName} modal`;
    const pageNameFormatted = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageName: pageNameFormatted,
            pageType,
            pageDetail
        }
    });
}

function addPickupPersonModalAnalytics() {
    const pageName = 'modal:access points-add pickup person:n/a:*';

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageName
        }
    });
}

/**
 * Fires an ASYNC_PAGE_LOAD analytics event when the Confirm Security Code modal is displayed.
 *
 * @param {string} [reason] - Optional description of why the modal was triggered (eVar). Defaults to CVV_CLICKED_PLACE_ORDER.
 */
function setConfirmSecurityCodeModalAnalytics(reason) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = anaConsts.PAGE_DETAIL.PAYMENT_CONFIRM_SECURITY_CODE;
    const linkData = reason || anaConsts.LinkData.CVV_CLICKED_PLACE_ORDER;
    const currentEventStrings = digitalData?.page?.attributes?.eventStrings;
    const eventStrings = Array.isArray(currentEventStrings) && currentEventStrings.length > 0 ? currentEventStrings : [anaConsts.Event.SC_CHECKOUT];

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageDetail,
            pageName: `${pageType}:${pageDetail}:n/a:*`.toLowerCase(),
            linkData: linkData.toLowerCase(),
            previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName,
            eventStrings
        }
    });
}

/**
 * Fires an ASYNC_PAGE_LOAD analytics event when an alt payment unavailable message
 * is displayed to the user. Captures the message text in eVar140.
 *
 * @param {string} message - The full unavailable payment message shown to the user (eVar140)
 */
function setAltPaymentUnavailableAnalytics(message) {
    if (!message) {
        return;
    }

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            eVar140: message
        }
    });
}

/**
 * Fires an ASYNC_PAGE_LOAD analytics event when the user opens the
 * "See Details" modal for unavailable alternate payment methods.
 */
function setAltPaymentDetailsModalAnalytics() {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = anaConsts.PAGE_DETAIL.ALT_PAYMENT_UNAVAILABLE_DETAILS;
    const pageName = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageDetail,
            pageName,
            previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName
        }
    });
}

/**
 * Tracks Klarna widget errors to analytics
 */
function setKlarnaErrorAnalytics(errorType, errorMessage) {
    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            fieldErrors: `${anaConsts.FIELD_ERRORS.CHECKOUT_PLACE_ORDER}:klarna.widget.${errorType}:payment`.toLowerCase(),
            errorMessages: errorMessage?.toLowerCase()
        }
    });
}

/**
 * Fires an ASYNC_PAGE_LOAD analytics event when the user expands Alternate Payment payment view
 */
function setAlternatePaymentExpandedViewAnalytics(paymentType) {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = `${anaConsts.PAGE_NAMES.PAYMENT_PAY_WITH} ${paymentType}`;
    const pageName = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    const eventStrings = [anaConsts.Event.SC_CHECKOUT];

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageDetail,
            eventStrings,
            pageName,
            previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName
        }
    });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) for payment vendor interactions.
 * Used for tracking express payment clicks, L2 payment button clicks, and save and continue events.
 *
 * @param {string} actionInfo - The action info for prop55 (e.g., 'checkout:express-payment:afterpay', 'checkout:payment:klarna', 'save and continue:venmo')
 * @param {Object} [options] - Optional analytics overrides.
 * @param {boolean} [options.isDefaultPayment=false] - When true, appends event252 to indicate the vendor is set as default payment.
 * @param {string} [options.paymentMethod] - When provided together with isDefaultPayment, is carried on the link
 *   tracking event so the tag manager can map it to prop56 (the default payment method) for this s.tl() call. Only
 *   populated when the vendor is being set as default so prop56 is not reported for non-default interactions.
 */
function trackPaymentVendorInteraction(actionInfo, { isDefaultPayment = false, paymentMethod } = {}) {
    const eventStrings = [anaConsts.Event.EVENT_71];

    if (isDefaultPayment) {
        eventStrings.push(anaConsts.Event.DEFAULT_PAYMENT_METHOD_USED);
    }

    const eventData = {
        eventStrings,
        linkName: 'D=c55',
        actionInfo: actionInfo.toLowerCase()
    };

    // Carry the payment method on the link event only when it is being set as default. linkTrackingEvent.js
    // maps it onto eventInfo.attributes.paymentMethod, which the tag manager reads to populate prop56 on this
    // s.tl() call, mirroring how prop55 reads the link event's actionInfo.
    if (isDefaultPayment && paymentMethod) {
        eventData.paymentMethod = paymentMethod;
    }

    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPageName = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;
    const prevAsyncPageName = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.pageName;

    if (previousPageName) {
        eventData.previousPage = previousPageName;
    }

    if (prevAsyncPageName) {
        eventData.pageName = prevAsyncPageName;
        eventData.pageDetail = prevAsyncPageName.split(':')?.[1];
    }

    if (actionInfo.toLowerCase().startsWith(anaConsts.ACTION_INFO.SAVE_AND_CONTINUE)) {
        paymentButtonState.tracked = true;
    }

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) whenever a CVV is submitted
 * alongside the updateCreditCardOnOrder API call.
 *
 * - prop55 ("c55") is set to 'cvv interaction' via actionInfo
 * - eventStrings contains event322
 * - pageName / pageDetail / previousPage are derived from the most recent
 *   asyncPageLoad event, matching the trackPaymentVendorInteraction pattern.
 */
function trackCVVInteraction() {
    const eventData = {
        eventStrings: [anaConsts.Event.EVENT_322],
        linkName: 'D=c55',
        actionInfo: 'cvv interaction'
    };

    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPageName = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;
    const prevAsyncPageName = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.pageName;

    if (previousPageName) {
        eventData.previousPage = previousPageName;
    }

    if (prevAsyncPageName) {
        eventData.pageName = prevAsyncPageName;
        eventData.pageDetail = prevAsyncPageName.split(':')?.[1];
    }

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) for Payment CTAs.
 * - Normalizes card types to the compact analytics values
 * - Emits prop55 via actionInfo: "<EN CTA text>:<payment method>"
 */
function trackPaymentCtaClick({ selectedAlternatePaymentType, selectedCardType } = {}) {
    const paymentMethod = selectedAlternatePaymentType
        ? String(selectedAlternatePaymentType).toLowerCase()
        : selectedCardType
            ? normalizeCardTypeForAnalytics(selectedCardType)
            : 'credit card';

    const englishCtaText = selectedAlternatePaymentType
        ? anaConsts.ACTION_INFO.PAYMENT_USE_THIS_PAYMENT_METHOD
        : anaConsts.ACTION_INFO.PAYMENT_USE_THIS_CARD;

    const prop55 = `${englishCtaText}:${paymentMethod}`.toLowerCase();

    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPage = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            linkName: 'D=c55',
            actionInfo: prop55,
            previousPage
        }
    });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) when the user submits the
 * add/edit credit card form.
 *
 * Includes prop55 plus optional events:
 * - event252: set default payment
 * - event310: save for future purchases
 * - event311: use different address (not in BOPIS)
 */
function trackUseThisCardSubmit({
    cardType, isMarkAsDefault, isSaveCreditCardForFutureUse, isUseShippingAddressAsBilling, isBopis
} = {}) {
    const normalizedCardType = normalizeCardTypeForAnalytics(cardType);

    const prop55 = `${anaConsts.ACTION_INFO.PAYMENT_USE_THIS_CARD}:${normalizedCardType}`.toLowerCase();
    const eventStrings = [];

    if (isMarkAsDefault) {
        eventStrings.push(anaConsts.Event.DEFAULT_PAYMENT_METHOD_USED);
    }

    if (isSaveCreditCardForFutureUse) {
        eventStrings.push(anaConsts.Event.SAVE_FOR_FUTURE_PURCHASES);
    }

    if (!isBopis && !isUseShippingAddressAsBilling) {
        eventStrings.push(anaConsts.Event.USE_DIFFERENT_ADDRESS);
    }

    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPage = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            linkName: 'D=c55',
            actionInfo: prop55,
            previousPage,
            ...(eventStrings.length > 0 && { eventStrings })
        }
    });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) when a gift card is successfully applied
 * to the order in the Frictionless Checkout payment flow.
 *
 * Sets prop55 via actionInfo: "gift card:apply"
 */
function trackGiftCardApply() {
    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPage = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            linkName: 'D=c55',
            actionInfo: anaConsts.ACTION_INFO.GIFT_CARD_APPLY,
            previousPage
        }
    });
}

/**
 * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) when a gift card removal is confirmed
 * and the API call to remove the gift card completes successfully.
 *
 * Sets prop55 via actionInfo: "gift card:remove"
 *
 * Note: This fires AFTER the removal confirmation, not when the modal opens.
 */
function trackGiftCardRemoveConfirmation() {
    const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');
    const previousPage = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.previousPageName;

    processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
        data: {
            linkName: 'D=c55',
            actionInfo: anaConsts.ACTION_INFO.GIFT_CARD_REMOVE,
            previousPage
        }
    });
}

/**
 * Fires an ASYNC_PAGE_LOAD event when the remove gift card confirmation modal opens.
 *
 * This uses ASYNC_PAGE_LOAD (vs LINK_TRACKING_EVENT) to track the modal view as a
 * virtual page, allowing it to appear in page-level analytics reports.
 *
 * Sets:
 * - pageDetail: "payment:remove gift card" (identifies the modal context)
 * - pageName: "checkout:payment:remove gift card:n/a:*"
 * - event (scCheckout): Tracks shopping cart checkout progression
 *
 * Triggers: When user clicks remove/delete on a gift card, opening the confirmation modal
 */
function trackGiftCardRemoveModal() {
    const pageType = anaConsts.PAGE_TYPES.CHECKOUT;
    const pageDetail = anaConsts.PAGE_DETAIL.PAYMENT_REMOVE_GIFT_CARD;

    const eventStrings = [anaConsts.Event.SC_CHECKOUT];
    const pageName = `${pageType}:${pageDetail}:n/a:*`.toLowerCase();

    processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
        data: {
            pageType,
            pageDetail,
            eventStrings,
            pageName,
            previousPageName: digitalData.page.attributes.sephoraPageInfo.pageName
        }
    });

    setAnalyticsForNextCall(pageName, pageDetail);
}

function triggerSOTAnalytics({
    eventName, sotType, pageName, origin, paymentMethod, ...data
}) {
    sendSOTLinkTrackingClickEvent({
        eventData: {
            linkName: eventName,
            actionInfo: eventName,
            specificEventName: eventName,
            sotType,
            pageName,
            origin,
            paymentMethod,
            ...data
        }
    });
}

function sotPaymentOptionCheckout(paymentMethod) {
    triggerSOTAnalytics({
        eventName: anaConsts.EVENT_NAMES.EXPRESS_PAYMENT_SELECTION,
        sotType: anaConsts.EVENT_NAMES.EXPRESS_PAYMENT_SELECTION,
        pageName: anaConsts.CONTEXT.CHECKOUT_PAGE,
        origin: anaConsts.EVENT_NAMES.PAYPAL_EXPRESS.PAGE_DETAIL.EXPRESS_CHECKOUT,
        paymentMethod
    });
}

export default {
    setAlternatePickupAnalytics,
    setChangeLinkAnalytics,
    setSubstitueModalClickAnalytics,
    setGiftCardPaymentAnalytics,
    setKlarnaErrorAnalytics,
    errorTrackingBeforeAPICall,
    trackPlaceOrderErrorAnalytics,
    setPickupInfoPageNameForInitialLoad,
    setAddOrEditPickupInfoAnalytics,
    setAddOrEditShippingAddressAnalytics,
    setPlaceOrderBtnClickanalytics,
    setSectionLevelErrorAnalytics,
    setAnalyticsForOrderConfirmation,
    thisAddressButtonAnalytics,
    newDefaultAddressAnalytics,
    addPickupPersonModalAnalytics,
    setConfirmSecurityCodeModalAnalytics,
    setAltPaymentUnavailableAnalytics,
    setAltPaymentDetailsModalAnalytics,
    setAlternatePaymentExpandedViewAnalytics,
    trackPaymentCtaClick,
    trackUseThisCardSubmit,
    setPaymentSectionButtonStateAnalytics,
    setPaymentPageNameForInitialLoad,
    resetPaymentButtonStateTracking,
    trackPaymentVendorInteraction,
    trackCVVInteraction,
    trackGiftCardApply,
    trackGiftCardRemoveModal,
    trackGiftCardRemoveConfirmation,
    triggerSOTAnalytics,
    sotPaymentOptionCheckout,
    setAnalyticsForNextCall
};
